import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AvatarName {
  id: string;
  name: string;
  description: string;
}

export interface AvatarRequest {
  id?: number;
  avatar_name: string;
  transcript_file_path?: string;
  video_file_path?: string;
  video_name?: string;
  file_size?: string;
  upload_date?: Date;
  status: 'pending' | 'in_progress' | 'completed' | 'delivered';
  requested_by: number;
  company_id: number;
  request_date: Date;
  completion_date?: Date;
  delivery_date?: Date;
  notes?: string;
  created_at?: Date;
  updated_at?: Date;
  requester?: {
    id: number;
    username: string;
    email: string;
  };
  company?: {
    id: number;
    name: string;
  };
}

export interface AvatarListResponse {
  success: boolean;
  data: AvatarRequest[];
  pagination: {
    total: number;
    page: number;
    totalPages: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AvatarService {
  private apiUrl = `${environment.apiUrl}/avatars`;

  constructor(private http: HttpClient) {}

  getAvatarNames(): Observable<{ success: boolean; data: AvatarName[] }> {
    return this.http.get<{ success: boolean; data: AvatarName[] }>(`${this.apiUrl}/avatar-names`);
  }

  getAvatarRequests(status?: string, page: number = 1, limit: number = 10): Observable<AvatarListResponse> {
    let params: any = { page, limit };
    if (status) {
      params.status = status;
    }
    return this.http.get<AvatarListResponse>(this.apiUrl, { params });
  }

  getAvatarRequest(id: number): Observable<{ success: boolean; data: AvatarRequest }> {
    return this.http.get<{ success: boolean; data: AvatarRequest }>(`${this.apiUrl}/${id}`);
  }

  createAvatarRequest(avatarName: string, transcript: File, notes?: string): Observable<any> {
    const formData = new FormData();
    formData.append('avatar_name', avatarName);
    if (transcript) {
      formData.append('transcript', transcript);
    }
    if (notes) {
      formData.append('notes', notes);
    }

    return this.http.post<any>(this.apiUrl, formData);
  }

  updateAvatarStatus(id: number, status: string): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/${id}/status`, { status });
  }

  uploadAvatarVideo(id: number, video: File): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append('video', video);

    return this.http.post(`${this.apiUrl}/${id}/upload-video`, formData, {
      reportProgress: true,
      observe: 'events'
    });
  }

  downloadAvatarVideo(id: number): Observable<HttpEvent<Blob>> {
    return this.http.get(`${this.apiUrl}/${id}/download-video`, {
      responseType: 'blob',
      observe: 'events',
      reportProgress: true
    });
  }

  downloadTranscript(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/download-transcript`, {
      responseType: 'blob'
    });
  }

  getTranscriptContent(id: number): Observable<{ success: boolean; data: { content?: string; type: string; fileName: string; downloadUrl?: string } }> {
    return this.http.get<{ success: boolean; data: { content?: string; type: string; fileName: string; downloadUrl?: string } }>(`${this.apiUrl}/${id}/transcript-content`);
  }

  deleteAvatarRequest(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${id}`);
  }

  getUploadedVideos(): Observable<AvatarListResponse> {
    return this.http.get<AvatarListResponse>(`${this.apiUrl}/uploaded-videos`);
  }

  uploadManualVideo(video: File, requestId?: number): Observable<any> {
    const formData = new FormData();
    formData.append('video', video);
    if (requestId) {
      formData.append('request_id', requestId.toString());
    }
    
    return this.http.post<any>(`${this.apiUrl}/upload-manual-video`, formData);
  }

  deleteUploadedVideo(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/uploaded-videos/${id}`);
  }

  getUploadProgress(event: HttpEvent<any>): number | null {
    if (event.type === HttpEventType.UploadProgress) {
      const percentDone = event.total ? Math.round(100 * event.loaded / event.total) : 0;
      return percentDone;
    }
    return null;
  }
}